/**
 * 90-Day Class Sign-up Popup - Gateway Edition
 * 
 * This popup serves as the GATEWAY for reserving seats in 90-day program sessions.
 * 
 * FLOW:
 *   For logged-in Shopify users:
 *     - Automatically check subscription status
 *     - Show subscriber or non-subscriber state
 * 
 *   For non-logged-in users:
 *     - Show email entry form
 *     - Check subscription via Skio API (by email)
 *     - Show subscriber or non-subscriber state
 * 
 * STATES:
 *   - email-entry: Collect email for subscription check
 *   - verifying: Checking subscription status
 *   - subscriber: Show reservation form (VIP quarterly subscriber)
 *   - non-subscriber: Show subscription upsell
 *   - success: Reservation confirmed
 * 
 * SECURITY:
 *   - All Klaviyo submissions go through server-side proxy
 *   - Server re-verifies subscription status before allowing reservation
 *   - No Klaviyo credentials exposed client-side
 * 
 * @version 6.0.0 - Server-Side Gated Reservations (Security Hardened)
 */

(function() {
  'use strict';

  const ClassSignupPopup = {
    // ═══════════════════════════════════════════════════════════════
    // CONFIGURATION
    // ═══════════════════════════════════════════════════════════════
    
    // Secure proxy for subscription verification AND Klaviyo submissions
    // All sensitive operations go through server-side proxy
    PROXY_URL: 'https://skio-proxy.vercel.app/api/skio',
    
    // Request timeout - fetched from proxy, with sensible fallback
    // This ensures frontend timeout always exceeds backend timeout
    REQUEST_TIMEOUT_MS: 70000, // Fallback: 70s (will be updated from proxy config)
    
    // Cache settings
    CACHE_KEY: 'quarterly_sub_status',
    CACHE_TTL: 10 * 60 * 1000, // 10 minutes
    
    // Email entry state
    pendingEmail: null,
    
    // Customer data (populated from Shopify or VIP Gateway config)
    customerEmail: null,
    customerFirstName: null,
    customerLastName: null,
    customerId: null,
    isLoggedIn: false,
    
    // Subscription status
    isQuarterlySubscriber: false,
    subscriptionVerified: false,
    
    // Current module data
    currentModule: null,
    
    // Location data (fetched via IP geolocation)
    locationData: {},
    
    // State management
    // States: 'loading' | 'email-entry' | 'code-sent' | 'verifying' | 'subscriber' | 'non-subscriber' | 'success'
    currentState: 'loading',

    // ═══════════════════════════════════════════════════════════════
    // INITIALIZATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Initialize the popup system
     */
    init() {
      // Get customer data from VIP Gateway config or Shopify customer object
      this.customerEmail = window.vipGatewayConfig?.customerEmail || '';
      this.customerFirstName = window.vipGatewayConfig?.customerFirstName || '';
      this.customerLastName = window.vipGatewayConfig?.customerLastName || '';
      this.customerId = window.vipGatewayConfig?.customerId || '';
      this.isLoggedIn = window.vipGatewayConfig?.isLoggedIn || false;
      
      // CRITICAL: Validate and clear stale cache on init
      // This ensures cache is in sync with current login state
      this.validateAndClearStaleCache();
      
      // Clear old auth cache from previous version (no longer used)
      try {
        sessionStorage.removeItem('skio_auth_session');
      } catch {
        // Ignore
      }
      
      // Inject popup HTML into the page
      this.injectPopupHTML();
      
      // Cache DOM elements
      this.cacheElements();
      
      // Bind event listeners
      this.bindEvents();
      
      // Pre-fetch location data and proxy config in background
      this.fetchLocationData();
      this.fetchProxyConfig();
      
      console.log('📅 Class Signup Gateway: Initialized', {
        isLoggedIn: this.isLoggedIn,
        email: this.customerEmail ? '***' : 'none'
      });
    },
    
    /**
     * Fetch timeout config from proxy (ensures client timeout > server timeout)
     * Runs in background - uses fallback if fetch fails
     */
    async fetchProxyConfig() {
      try {
        const response = await fetch(this.PROXY_URL, { method: 'GET' });
        if (response.ok) {
          const health = await response.json();
          if (health.config?.recommendedClientTimeoutMs) {
            this.REQUEST_TIMEOUT_MS = health.config.recommendedClientTimeoutMs;
            console.log('📅 Gateway: Timeout configured from proxy:', this.REQUEST_TIMEOUT_MS + 'ms');
          }
        }
      } catch {
        // Silent fail - use fallback timeout
      }
    },

    /**
     * Validate cache against current login state and clear if stale
     * This prevents showing subscriber state after logout
     */
    validateAndClearStaleCache() {
      try {
        const cached = sessionStorage.getItem(this.CACHE_KEY);
        if (!cached) return;
        
        const { email, isLoggedIn: cachedLoginState } = JSON.parse(cached);
        
        // Clear cache if:
        // 1. User logged out (was logged in, now not)
        // 2. User logged in as different account (email changed)
        // 3. Login state changed
        
        const loginStateChanged = cachedLoginState !== this.isLoggedIn;
        const emailChanged = email && this.customerEmail && email !== this.customerEmail;
        const wasLoggedInNowLoggedOut = cachedLoginState === true && !this.isLoggedIn;
        
        if (loginStateChanged || emailChanged || wasLoggedInNowLoggedOut) {
          console.log('📅 Gateway: Clearing stale cache (login state changed)', {
            wasLoggedIn: cachedLoginState,
            isLoggedIn: this.isLoggedIn,
            emailChanged
          });
          this.clearCache();
        }
      } catch (e) {
        // If we can't parse cache, clear it
        this.clearCache();
      }
    },

    /**
     * Clear all cached data
     */
    clearCache() {
      try {
        sessionStorage.removeItem(this.CACHE_KEY);
      } catch {
        // Ignore errors
      }
    },

    /**
     * Cache all DOM elements
     */
    cacheElements() {
      this.elements = {
        overlay: document.getElementById('class-signup-overlay'),
        popup: document.getElementById('class-signup-popup'),
        closeBtn: document.getElementById('class-signup-close'),
        
        // State containers
        loadingState: document.getElementById('class-signup-loading'),
        emailEntryState: document.getElementById('class-signup-email-entry'),
        codeSentState: document.getElementById('class-signup-code-sent'),
        verifyingState: document.getElementById('class-signup-verifying'),
        guestState: document.getElementById('class-signup-guest'),
        nonSubscriberState: document.getElementById('class-signup-non-subscriber'),
        subscriberState: document.getElementById('class-signup-subscriber'),
        successState: document.getElementById('class-signup-success'),
        
        // Email entry state elements
        authEmailForm: document.getElementById('class-signup-auth-email-form'),
        authEmailInput: document.getElementById('class-signup-auth-email'),
        authEmailSubmit: document.getElementById('class-signup-auth-email-submit'),
        
        // Code verification state elements
        codeInputs: document.querySelectorAll('.class-signup-popup__code-input'),
        codeVerifyBtn: document.getElementById('class-signup-verify-code'),
        codeResendBtn: document.getElementById('class-signup-resend-code'),
        codeResendTimer: document.getElementById('class-signup-resend-timer'),
        codeUserEmail: document.getElementById('class-signup-code-email'),
        codeChangeEmailBtn: document.getElementById('class-signup-change-email'),
        codeError: document.getElementById('class-signup-code-error'),
        
        // Verifying state elements
        verifyingStatus: document.getElementById('class-signup-verifying-status'),
        // Error/retry state elements
        errorState: document.getElementById('class-signup-error'),
        errorRetryBtn: document.getElementById('class-signup-error-retry'),
        
        // Form elements (subscriber state)
        form: document.getElementById('class-signup-form'),
        emailInput: document.getElementById('class-signup-email'),
        submitBtn: document.getElementById('class-signup-submit'),
        editEmailBtn: document.getElementById('class-signup-edit-email'),
        verifiedBadge: document.querySelector('.class-signup-popup__verified-badge'),
        
        // Dynamic content elements
        heroImage: document.getElementById('class-signup-hero-image'),
        badge: document.getElementById('class-signup-badge'),
        badgeText: document.getElementById('class-signup-badge-text'),
        title: document.getElementById('class-signup-title'),
        expertAvatar: document.getElementById('class-signup-expert-avatar'),
        expertName: document.getElementById('class-signup-expert-name'),
        expertRole: document.getElementById('class-signup-expert-role'),
        description: document.getElementById('class-signup-description'),
        dateValue: document.getElementById('class-signup-date'),
        durationValue: document.getElementById('class-signup-duration'),
        
        // Guest state elements (legacy, kept for backwards compatibility)
        guestLoginBtn: document.getElementById('class-signup-login-btn'),
        guestSubscribeBtn: document.getElementById('class-signup-guest-subscribe'),
        
        // Non-subscriber state elements
        nonSubSubscribeBtn: document.getElementById('class-signup-subscribe-btn'),
        nonSubUserName: document.getElementById('class-signup-user-name')
      };
    },

    /**
     * Inject the popup HTML structure with all states
     */
    injectPopupHTML() {
      const popupHTML = `
        <div class="class-signup-overlay" id="class-signup-overlay">
          <div class="class-signup-popup" id="class-signup-popup" role="dialog" aria-modal="true" aria-labelledby="class-signup-title">
            
            <!-- Close Button -->
            <button class="class-signup-popup__close" id="class-signup-close" aria-label="Close popup">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            
            <!-- Hero Section -->
            <div class="class-signup-popup__hero">
              <img src="" alt="" class="class-signup-popup__hero-image" id="class-signup-hero-image">
              <div class="class-signup-popup__hero-gradient"></div>
              <div class="class-signup-popup__badge" id="class-signup-badge">
                <span class="class-signup-popup__badge-dot"></span>
                <span id="class-signup-badge-text">MODULE 1 | DECEMBER 2025</span>
              </div>
            </div>
            
            <!-- Content Section -->
            <div class="class-signup-popup__content">
              
              <!-- Session Title -->
              <h2 class="class-signup-popup__title" id="class-signup-title">Session Title</h2>
              
              <!-- Expert Info -->
              <div class="class-signup-popup__expert">
                <img src="" alt="" class="class-signup-popup__expert-avatar" id="class-signup-expert-avatar">
                <div class="class-signup-popup__expert-info">
                  <p class="class-signup-popup__expert-name" id="class-signup-expert-name">Expert Name</p>
                  <p class="class-signup-popup__expert-role" id="class-signup-expert-role">Expert Role</p>
                </div>
              </div>
              
              <!-- Description -->
              <p class="class-signup-popup__description" id="class-signup-description">Session description...</p>
              
              <!-- Session Details -->
              <div class="class-signup-popup__details">
                <div class="class-signup-popup__detail">
                  <div class="class-signup-popup__detail-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <path d="M16 2v4M8 2v4M3 10h18"/>
                    </svg>
                  </div>
                  <div class="class-signup-popup__detail-text">
                    <span class="class-signup-popup__detail-label">Date</span>
                    <span class="class-signup-popup__detail-value" id="class-signup-date">December 18, 2025</span>
                  </div>
                </div>
                <div class="class-signup-popup__detail">
                  <div class="class-signup-popup__detail-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                  </div>
                  <div class="class-signup-popup__detail-text">
                    <span class="class-signup-popup__detail-label">Duration</span>
                    <span class="class-signup-popup__detail-value" id="class-signup-duration">60 minutes | Live Q&A</span>
                  </div>
                </div>
              </div>
              
              <div class="class-signup-popup__divider"></div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: LOADING -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-loading" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__loading">
                  <div class="class-signup-popup__spinner">
                    <svg viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></circle>
                    </svg>
                  </div>
                  <p class="class-signup-popup__loading-text">Verifying your membership...</p>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: EMAIL ENTRY (New - In-Modal Authentication) -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-email-entry" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__auth">
                  <div class="class-signup-popup__auth-header">
                    <div class="class-signup-popup__auth-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="M22 6l-10 7L2 6"/>
                      </svg>
                    </div>
                    <h3 class="class-signup-popup__auth-title">Verify Your VIP Status</h3>
                    <p class="class-signup-popup__auth-subtitle">
                      Enter your email to check your membership and reserve your seat.
                    </p>
                  </div>
                  
                  <form id="class-signup-auth-email-form" class="class-signup-popup__auth-form">
                    <div class="class-signup-popup__input-group">
                      <label class="class-signup-popup__input-label" for="class-signup-auth-email">Email Address</label>
                      <div class="class-signup-popup__input-wrapper">
                        <span class="class-signup-popup__input-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="M22 6l-10 7L2 6"/>
                          </svg>
                        </span>
                        <input 
                          type="email" 
                          id="class-signup-auth-email" 
                          name="email"
                          class="class-signup-popup__input" 
                          placeholder="your@email.com"
                          required
                          autocomplete="email"
                        >
                      </div>
                    </div>
                    
                    <button type="submit" class="class-signup-popup__btn class-signup-popup__btn--primary class-signup-popup__btn--gold" id="class-signup-auth-email-submit">
                      <span>Continue</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </form>
                  
                  <div class="class-signup-popup__auth-features">
                    <div class="class-signup-popup__auth-feature">
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M10 2L12.09 6.26L17 6.97L13.5 10.35L14.18 15.25L10 13.05L5.82 15.25L6.5 10.35L3 6.97L7.91 6.26L10 2Z" fill="#eeb87a" stroke="#eeb87a"/>
                      </svg>
                      <span>Exclusive VIP Access</span>
                    </div>
                    <div class="class-signup-popup__auth-feature">
                      <svg viewBox="0 0 20 20" fill="none" stroke="#22c55e" stroke-width="1.5">
                        <path d="M7.5 10L9.16667 11.6667L12.5 8.33333"/>
                        <circle cx="10" cy="10" r="8"/>
                      </svg>
                      <span>Secure & Private</span>
                    </div>
                    <div class="class-signup-popup__auth-feature">
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="10" cy="10" r="8"/>
                        <path d="M10 6v4l2.5 2.5"/>
                      </svg>
                      <span>Takes 30 seconds</span>
                    </div>
                  </div>
                  
                  <p class="class-signup-popup__auth-note">
                    Not a VIP member yet? <a href="/products/essentials" class="class-signup-popup__gateway-link">Start Quarterly Subscription</a>
                  </p>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: CODE VERIFICATION (New - 4-digit OTP input) -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-code-sent" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__auth class-signup-popup__auth--code">
                  <div class="class-signup-popup__auth-header">
                    <div class="class-signup-popup__auth-icon class-signup-popup__auth-icon--sent">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                    <h3 class="class-signup-popup__auth-title">Check Your Email</h3>
                    <p class="class-signup-popup__auth-subtitle">
                      We sent a 4-digit code to<br>
                      <strong id="class-signup-code-email">your@email.com</strong>
                    </p>
                  </div>
                  
                  <div class="class-signup-popup__code-container">
                    <label class="class-signup-popup__code-label">Enter verification code</label>
                    <div class="class-signup-popup__code-inputs">
                      <input type="text" inputmode="numeric" maxlength="1" class="class-signup-popup__code-input" data-index="0" autocomplete="off">
                      <input type="text" inputmode="numeric" maxlength="1" class="class-signup-popup__code-input" data-index="1" autocomplete="off">
                      <input type="text" inputmode="numeric" maxlength="1" class="class-signup-popup__code-input" data-index="2" autocomplete="off">
                      <input type="text" inputmode="numeric" maxlength="1" class="class-signup-popup__code-input" data-index="3" autocomplete="off">
                    </div>
                    <p id="class-signup-code-error" class="class-signup-popup__code-error" style="display: none;">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      Invalid code. Please try again.
                    </p>
                  </div>
                  
                  <button type="button" class="class-signup-popup__btn class-signup-popup__btn--primary class-signup-popup__btn--gold" id="class-signup-verify-code" disabled>
                    <span>Verify & Continue</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                  
                  <div class="class-signup-popup__code-actions">
                    <p class="class-signup-popup__code-resend">
                      Didn't receive the code? 
                      <button type="button" id="class-signup-resend-code" class="class-signup-popup__code-resend-btn">
                        Resend code
                      </button>
                      <span id="class-signup-resend-timer" class="class-signup-popup__code-timer" style="display: none;">
                        (wait <span class="class-signup-popup__code-timer-seconds">60</span>s)
                      </span>
                    </p>
                    <button type="button" id="class-signup-change-email" class="class-signup-popup__code-change-email">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      Use a different email
                    </button>
                  </div>
                  
                  <div class="class-signup-popup__code-hint">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">
                      <circle cx="10" cy="10" r="8"/>
                      <path d="M10 13V10M10 7h.01"/>
                    </svg>
                    <span>Check your spam folder if you don't see the email. The code expires in 3 minutes.</span>
                  </div>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: VERIFYING (Checking subscription after auth) -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-verifying" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__loading class-signup-popup__loading--verifying">
                  <div class="class-signup-popup__spinner class-signup-popup__spinner--gold">
                    <svg viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></circle>
                    </svg>
                  </div>
                  <div class="class-signup-popup__verifying-steps">
                    <p id="class-signup-verifying-status" class="class-signup-popup__loading-text">Verifying your email...</p>
                    <div class="class-signup-popup__verifying-progress">
                      <div class="class-signup-popup__verifying-progress-bar"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: GUEST (Legacy - kept for backwards compatibility) -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-guest" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__gateway">
                  <div class="class-signup-popup__gateway-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="3" y="11" width="18" height="11" rx="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                      <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3 class="class-signup-popup__gateway-title">Sign In to Reserve Your Seat</h3>
                  <p class="class-signup-popup__gateway-text">
                    Access to live sessions is exclusive to IM8 Quarterly subscribers. Sign in to check your membership status.
                  </p>
                  
                  <div class="class-signup-popup__gateway-actions">
                    <button type="button" class="class-signup-popup__btn class-signup-popup__btn--primary" id="class-signup-login-btn">
                      <span>Sign In to Reserve</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                    <a href="/products/essentials" class="class-signup-popup__btn class-signup-popup__btn--secondary" id="class-signup-guest-subscribe">
                      <span>Start Quarterly Subscription</span>
                    </a>
                  </div>
                  
                  <p class="class-signup-popup__gateway-note">
                    Already a subscriber? <a href="/account/login" class="class-signup-popup__gateway-link">Sign in here</a>
                  </p>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: NON-SUBSCRIBER (Logged in but not quarterly) -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-non-subscriber" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__gateway class-signup-popup__gateway--upgrade">
                  <div class="class-signup-popup__gateway-icon class-signup-popup__gateway-icon--gold">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                    </svg>
                  </div>
                  <h3 class="class-signup-popup__gateway-title">
                    Hi <span id="class-signup-user-name">there</span>, Unlock Exclusive Access
                  </h3>
                  <p class="class-signup-popup__gateway-text">
                    Live sessions are reserved for IM8 Quarterly subscribers. Upgrade to unlock this session and all upcoming masterclasses.
                  </p>
                  
                  <div class="class-signup-popup__gateway-actions">
                    <a href="/products/essentials" class="class-signup-popup__btn class-signup-popup__btn--primary class-signup-popup__btn--gold" id="class-signup-subscribe-btn">
                      <span>Start Quarterly Subscription</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </a>
                  </div>
                  
                  <p class="class-signup-popup__gateway-note">
                    Questions? <a href="#program-faq" class="class-signup-popup__gateway-link">View FAQ</a> or <a href="https://im8health.com/pages/contact-us" class="class-signup-popup__gateway-link">contact us</a>
                  </p>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: SUBSCRIBER (Verified quarterly subscriber) -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-subscriber" class="class-signup-popup__state" style="display: none;">
                <div class="class-signup-popup__form-section">
                  <div class="class-signup-popup__subscriber-badge">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M10 2L12.09 6.26L17 6.97L13.5 10.35L14.18 15.25L10 13.05L5.82 15.25L6.5 10.35L3 6.97L7.91 6.26L10 2Z" fill="#22c55e" stroke="#22c55e"/>
                    </svg>
                    <span>QUARTERLY MEMBER</span>
                  </div>
                  
                  <h3 class="class-signup-popup__form-heading">Reserve Your Seat</h3>
                  <p class="class-signup-popup__form-subheading">Confirm your email to secure your spot in this exclusive session.</p>
                  
                  <form id="class-signup-form">
                    <div class="class-signup-popup__input-group">
                      <label class="class-signup-popup__input-label" for="class-signup-email">Email Address</label>
                      <div class="class-signup-popup__input-wrapper">
                        <span class="class-signup-popup__input-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="M22 6l-10 7L2 6"/>
                          </svg>
                        </span>
                        <input 
                          type="email" 
                          id="class-signup-email" 
                          name="email"
                          class="class-signup-popup__input class-signup-popup__input--verified" 
                          placeholder="your@email.com"
                          required
                          readonly
                        >
                        <span class="class-signup-popup__verified-badge">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <path d="M20 6L9 17l-5-5"/>
                          </svg>
                          Verified
                        </span>
                      </div>
                      <button type="button" class="class-signup-popup__edit-email" id="class-signup-edit-email">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Use a different email
                      </button>
                    </div>
                    
                    <button type="submit" class="class-signup-popup__submit" id="class-signup-submit">
                      <span class="class-signup-popup__submit-text">Confirm Reservation</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </form>
                  
                  <p class="class-signup-popup__privacy">
                    By reserving your seat, you agree to receive session reminders. 
                    <a href="/pages/privacy-policy" target="_blank">Privacy Policy</a>
                  </p>
                </div>
              </div>
              
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <!-- STATE: SUCCESS -->
              <!-- ═══════════════════════════════════════════════════════════════ -->
              <div id="class-signup-success" class="class-signup-popup__state class-signup-popup__success" style="display: none;">
                <div class="class-signup-popup__success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <h3 class="class-signup-popup__success-title">You're In!</h3>
                <p class="class-signup-popup__success-message">
                  Your seat has been reserved. We'll send you a reminder before the session starts.
                </p>
                <button type="button" class="class-signup-popup__success-close" id="class-signup-success-close">
                  Continue Exploring
                </button>
              </div>
              
            </div>
          </div>
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', popupHTML);
    },

    // ═══════════════════════════════════════════════════════════════
    // EVENT BINDINGS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Close button
      this.elements.closeBtn?.addEventListener('click', () => this.close());
      
      // Click outside to close
      this.elements.overlay?.addEventListener('click', (e) => {
        if (e.target === this.elements.overlay) {
          this.close();
        }
      });
      
      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
      
      // ═══════════════════════════════════════════════════════════════
      // EMAIL ENTRY STATE - Authentication flow
      // ═══════════════════════════════════════════════════════════════
      
      // Email entry form submission
      this.elements.authEmailForm?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleAuthEmailSubmit();
      });
      
      // ═══════════════════════════════════════════════════════════════
      // SUBSCRIBER STATE - Reservation form
      // ═══════════════════════════════════════════════════════════════
      
      // Form submission (subscriber state)
      this.elements.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
      
      // Real-time email validation
      this.elements.emailInput?.addEventListener('input', () => this.validateEmailRealtime());
      this.elements.emailInput?.addEventListener('blur', () => this.validateEmailOnBlur());
      
      // Edit email button
      this.elements.editEmailBtn?.addEventListener('click', () => this.enableEmailEdit());
      
      // Success close button
      document.getElementById('class-signup-success-close')?.addEventListener('click', () => this.close());
      
      // FAQ link - close popup and scroll to FAQ section (using event delegation)
      this.elements.popup?.addEventListener('click', (e) => {
        const faqLink = e.target.closest('a[href="#program-faq"]');
        if (faqLink) {
          e.preventDefault();
          console.log('📅 Class Signup: FAQ link clicked, closing popup and scrolling to FAQ');
          this.close();
          
          // Smooth scroll to FAQ section after popup closes
          setTimeout(() => {
            const faqSection = document.getElementById('program-faq');
            console.log('📅 Class Signup: Looking for FAQ section, found:', faqSection);
            if (faqSection) {
              // Use scrollTo for more reliable scrolling
              const rect = faqSection.getBoundingClientRect();
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
              const targetY = rect.top + scrollTop - 80; // 80px offset for header
              
              window.scrollTo({
                top: targetY,
                behavior: 'smooth'
              });
              console.log('📅 Class Signup: Scrolling to FAQ at Y:', targetY);
            } else {
              console.warn('📅 Class Signup: FAQ section (#program-faq) not found on this page');
            }
          }, 350); // Wait for popup close animation
        }
      });
      
      // Guest state - Login button (legacy, kept for compatibility)
      this.elements.guestLoginBtn?.addEventListener('click', () => {
        // Instead of redirect, show email entry state
        this.showState('email-entry');
      });
      
      // Attach to Reserve Seat buttons in curriculum
      this.attachToReserveButtons();
    },

    /**
     * Attach click handlers to all Reserve Seat buttons
     */
    attachToReserveButtons() {
      // Find all active Reserve Seat buttons in curriculum section
      const reserveButtons = document.querySelectorAll('.program-curriculum__card-cta--active');
      
      reserveButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Get module data from the parent card
          const card = button.closest('.program-curriculum__card');
          if (card) {
            const moduleData = this.extractModuleData(card);
            this.open(moduleData);
          }
        });
      });
      
      // Find all buttons with data-class-signup-trigger attribute (e.g., announcement bar CTA)
      const triggerButtons = document.querySelectorAll('[data-class-signup-trigger]');
      
      triggerButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Get module index from the trigger
          const moduleIndex = button.dataset.moduleIndex || '1';
          
          // Find the corresponding curriculum card and extract data from it
          const moduleData = this.getModuleDataByIndex(moduleIndex);
          if (moduleData) {
            this.open(moduleData);
          }
        });
      });
      
      console.log(`📅 Class Signup Gateway: Attached to ${reserveButtons.length} curriculum buttons and ${triggerButtons.length} trigger buttons`);
    },

    // ═══════════════════════════════════════════════════════════════
    // MODULE DATA EXTRACTION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Get module data by finding the curriculum card with matching index
     */
    getModuleDataByIndex(moduleIndex) {
      const card = document.querySelector(`.program-curriculum__card[data-module-index="${moduleIndex}"]`);
      
      if (card) {
        return this.extractModuleData(card);
      }
      
      // Fallback: find first active card if no match
      const activeCard = document.querySelector('.program-curriculum__card-cta--active')?.closest('.program-curriculum__card');
      if (activeCard) {
        console.log('📅 Class Signup Gateway: Using first active card as fallback');
        return this.extractModuleData(activeCard);
      }
      
      console.warn('📅 Class Signup Gateway: No curriculum card found for module', moduleIndex);
      return null;
    },

    /**
     * Extract module data from a curriculum card
     */
    extractModuleData(card) {
      const moduleIndex = card.dataset.moduleIndex || '1';
      const expertSlug = card.dataset.expertSlug || '';
      const sessionDate = card.dataset.sessionDate || '';
      const sessionTime = card.dataset.sessionTime || '';
      
      // Get text content from various elements
      const moduleLabel = card.querySelector('.program-curriculum__module-badge-text')?.textContent?.trim() || '';
      const sessionTitle = card.querySelector('.program-curriculum__card-title')?.textContent?.trim() || '';
      const expertName = card.querySelector('.program-curriculum__expert-name')?.textContent?.trim() || '';
      const expertRole = card.querySelector('.program-curriculum__expert-role')?.textContent?.trim() || '';
      const description = card.querySelector('.program-curriculum__card-description')?.textContent?.trim() || '';
      
      // Get images
      const expertImage = card.querySelector('.program-curriculum__card-image-img')?.src || '';
      
      // Date text: just the date (e.g., "January 19, 2026")
      let dateText = sessionDate;
      if (!dateText) {
        // Fallback: parse from module label (e.g., "MODULE 1 | DECEMBER 2025")
        const dateParts = moduleLabel.split('|');
        dateText = dateParts[1]?.trim() || 'Coming Soon';
      }
      
      // Duration: include time if available (e.g., "8pm ET | 60 minutes | Live Q&A")
      let duration = '60 minutes | Live Q&A';
      if (sessionTime) {
        duration = `${sessionTime} | 60 minutes | Live Q&A`;
      }
      
      return {
        moduleIndex,
        moduleLabel,
        sessionTitle,
        expertName,
        expertRole,
        description,
        expertImage,
        expertSlug,
        dateText,
        duration
      };
    },

    // ═══════════════════════════════════════════════════════════════
    // POPUP OPEN/CLOSE
    // ═══════════════════════════════════════════════════════════════

    /**
     * Open the popup with module data
     */
    async open(moduleData) {
      this.currentModule = moduleData;
      
      // Populate popup content with module data
      this.populateContent(moduleData);
      
      // Hide all states, show loading initially
      this.hideAllStates();
      this.elements.loadingState.style.display = 'block';
      
      // Show popup
      this.elements.overlay.classList.add('class-signup-overlay--active');
      document.body.style.overflow = 'hidden';
      
      console.log('📅 Class Signup Gateway: Opened for', moduleData.sessionTitle);
      
      // Determine which state to show based on user status
      await this.determineAndShowState();
    },

    /**
     * Close the popup
     */
    close() {
      this.elements.overlay.classList.remove('class-signup-overlay--active');
      document.body.style.overflow = '';
      
      console.log('📅 Class Signup Gateway: Closed');
    },

    /**
     * Check if popup is currently open
     */
    isOpen() {
      return this.elements.overlay?.classList.contains('class-signup-overlay--active');
    },

    /**
     * Hide all state containers
     */
    hideAllStates() {
      this.elements.loadingState.style.display = 'none';
      this.elements.emailEntryState && (this.elements.emailEntryState.style.display = 'none');
      this.elements.codeSentState && (this.elements.codeSentState.style.display = 'none');
      this.elements.verifyingState && (this.elements.verifyingState.style.display = 'none');
      this.elements.errorState && (this.elements.errorState.style.display = 'none');
      this.elements.guestState.style.display = 'none';
      this.elements.nonSubscriberState.style.display = 'none';
      this.elements.subscriberState.style.display = 'none';
      this.elements.successState.style.display = 'none';
      
      // Also hide dynamic error state if it exists
      const dynamicError = document.getElementById('class-signup-error-dynamic');
      if (dynamicError) dynamicError.style.display = 'none';
    },

    // ═══════════════════════════════════════════════════════════════
    // STATE MANAGEMENT
    // ═══════════════════════════════════════════════════════════════

    /**
     * Determine which state to show based on user's login and subscription status
     */
    async determineAndShowState() {
      // STATE 1: Not logged in to Shopify - show email entry
      if (!this.isLoggedIn) {
        console.log('📅 Gateway State: EMAIL-ENTRY (not logged in)');
        this.showState('email-entry');
        return;
      }
      
      // User is logged in to Shopify - check subscription status
      console.log('📅 Gateway: Checking subscription status...');
      
      // Check cache first
      const cached = this.getCachedSubscriptionStatus();
      if (cached !== null) {
        console.log('📅 Gateway: Cache hit -', cached ? 'subscriber' : 'non-subscriber');
        cached ? this.showState('subscriber') : this.showState('non-subscriber');
        return;
      }
      
      // Verify subscription via API
      try {
        const hasQuarterly = await this.verifySubscription();
        
        // Cache the result
        this.setCachedSubscriptionStatus(hasQuarterly);
        
        // Show appropriate state
        if (hasQuarterly) {
          console.log('📅 Gateway State: SUBSCRIBER (verified quarterly)');
          this.showState('subscriber');
        } else {
          console.log('📅 Gateway State: NON-SUBSCRIBER (logged in, no quarterly)');
          this.showState('non-subscriber');
        }
      } catch (error) {
        console.error('📅 Gateway: Subscription verification failed:', error);
        // Show error state with retry option instead of defaulting to non-subscriber
        this.showState('error');
      }
    },

    /**
     * Show a specific state
     */
    showState(state) {
      this.hideAllStates();
      this.currentState = state;
      
      switch (state) {
        case 'loading':
          this.elements.loadingState.style.display = 'block';
          break;
        
        // ═══════════════════════════════════════════════════════════════
        // NEW AUTHENTICATION STATES
        // ═══════════════════════════════════════════════════════════════
        
        case 'email-entry':
          if (this.elements.emailEntryState) {
            this.elements.emailEntryState.style.display = 'block';
            // Clear and focus email input
            const authEmailInput = document.getElementById('class-signup-auth-email');
            if (authEmailInput) {
              authEmailInput.value = this.pendingEmail || '';
              setTimeout(() => authEmailInput.focus(), 400);
            }
          }
          break;
          
        case 'verifying':
          if (this.elements.verifyingState) {
            this.elements.verifyingState.style.display = 'block';
            this.updateVerifyingStatus('Verifying your email...');
          }
          break;
        
        case 'error':
          if (this.elements.errorState) {
            this.elements.errorState.style.display = 'block';
          } else {
            // Fallback: create error state dynamically if HTML element doesn't exist
            this.showDynamicErrorState();
          }
          break;
          
        // ═══════════════════════════════════════════════════════════════
        // EXISTING STATES
        // ═══════════════════════════════════════════════════════════════
          
        case 'guest':
          this.elements.guestState.style.display = 'block';
          break;
          
        case 'non-subscriber':
          // Personalize with user's name or email
          if (this.elements.nonSubUserName) {
            if (this.customerFirstName) {
            this.elements.nonSubUserName.textContent = this.customerFirstName;
            } else if (this.pendingEmail) {
              // Use email prefix as fallback name
              const emailName = this.pendingEmail.split('@')[0];
              this.elements.nonSubUserName.textContent = emailName;
            } else {
              this.elements.nonSubUserName.textContent = 'there';
            }
          }
          this.elements.nonSubscriberState.style.display = 'block';
          break;
          
        case 'subscriber':
          // Pre-fill email from Shopify customer or pending auth email
          const emailToUse = this.customerEmail || this.pendingEmail;
          if (emailToUse) {
            this.elements.emailInput.value = emailToUse;
            this.elements.emailInput.classList.add('class-signup-popup__input--verified');
            if (this.elements.verifiedBadge) {
              this.elements.verifiedBadge.style.display = 'flex';
            }
            this.elements.emailInput.readOnly = true;
          } else {
            this.elements.emailInput.value = '';
            this.elements.emailInput.classList.remove('class-signup-popup__input--verified');
            if (this.elements.verifiedBadge) {
              this.elements.verifiedBadge.style.display = 'none';
            }
            this.elements.emailInput.readOnly = false;
          }
          
          // Reset form state
          this.elements.submitBtn?.classList.remove('class-signup-popup__submit--loading');
          if (this.elements.submitBtn) {
            this.elements.submitBtn.disabled = false;
          }
          
          this.elements.subscriberState.style.display = 'block';
          
          // Focus email input if not pre-filled
          if (!this.elements.emailInput.readOnly) {
            setTimeout(() => this.elements.emailInput.focus(), 400);
          }
          break;
          
        case 'success':
          this.elements.successState.style.display = 'block';
          break;
      }
    },
    
    /**
     * Update verifying status message
     */
    updateVerifyingStatus(message) {
      if (this.elements.verifyingStatus) {
        this.elements.verifyingStatus.textContent = message;
      }
    },
    
    /**
     * Show dynamic error state (fallback if HTML element doesn't exist)
     * WORLD-CLASS UX: Clear messaging, easy retry, support option
     */
    showDynamicErrorState() {
      // CRITICAL: Hide all other states first to prevent overlap
      this.hideAllStates();
      
      // Also hide the dynamic error if it exists (will recreate fresh)
      const existing = document.getElementById('class-signup-error-dynamic');
      if (existing) existing.remove();
      
      // Create error state dynamically
      const popup = document.querySelector('.class-signup-popup__content');
      if (!popup) return;
      
      const errorDiv = document.createElement('div');
      errorDiv.id = 'class-signup-error-dynamic';
      errorDiv.className = 'class-signup-popup__state';
      errorDiv.innerHTML = `
        <div style="text-align: center; padding: 2rem 1.5rem;">
          <div style="width: 64px; height: 64px; margin: 0 auto 1.25rem; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          
          <h3 style="margin: 0 0 0.5rem; font-size: 1.35rem; font-weight: 600; color: rgba(255, 255, 255, 0.95);">
            Verification Interrupted
          </h3>
          
          <p style="color: rgba(255, 255, 255, 0.7); margin: 0 0 1.5rem; font-size: 0.95rem; line-height: 1.5;">
            We couldn't complete the membership check.<br>
            This is usually a temporary connection issue.
          </p>
          
          <button id="class-signup-error-retry-dynamic" class="class-signup-popup__btn class-signup-popup__btn--primary" style="min-width: 220px; padding: 14px 28px; font-size: 1rem; font-weight: 600;">
            Try Again
          </button>
          
          <div style="margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid rgba(255, 255, 255, 0.15);">
            <p style="color: rgba(255, 255, 255, 0.5); font-size: 0.85rem; margin: 0 0 0.5rem;">
              Still having trouble?
            </p>
            <a href="https://im8health.com/pages/contact-us" 
               style="color: #eeb87a; text-decoration: none; font-size: 0.85rem; font-weight: 500;">
              Contact Support →
            </a>
          </div>
        </div>
      `;
      popup.appendChild(errorDiv);
      
      // Bind retry handler with loading state
      const retryBtn = document.getElementById('class-signup-error-retry-dynamic');
      retryBtn?.addEventListener('click', async () => {
        retryBtn.disabled = true;
        retryBtn.innerHTML = '<span style="opacity: 0.7;">Checking...</span>';
        await this.retrySubscriptionCheck();
      });
      
      errorDiv.style.display = 'block';
    },
    
    /**
     * Retry subscription check after error
     */
    async retrySubscriptionCheck() {
      console.log('📅 Gateway: Retrying subscription check...');
      
      // Clear any cached error state
      this.lastCheckWasTimeout = false;
      
      if (this.isLoggedIn) {
        // For logged-in users, re-run the full verification
        await this.determineAndShowState();
      } else if (this.pendingEmail) {
        // For email-entry users, re-check with the pending email
        await this.handleAuthEmailSubmit();
      } else {
        // Fallback to email entry
        this.showState('email-entry');
      }
    },
    
    // ═══════════════════════════════════════════════════════════════
    // EMAIL SUBSCRIPTION CHECK HANDLERS
    // ═══════════════════════════════════════════════════════════════
    
    /**
     * Handle email form submission - check subscription status
     * OPTIMIZED: Removed artificial delay, faster state transitions
     */
    async handleAuthEmailSubmit() {
      const authEmailInput = document.getElementById('class-signup-auth-email');
      const email = authEmailInput?.value?.trim();
      
      if (!email || !this.validateEmail(email)) {
        // Show error
        authEmailInput?.classList.add('class-signup-popup__input--error');
        return;
      }
      
      authEmailInput?.classList.remove('class-signup-popup__input--error');
      this.pendingEmail = email;
      this.customerEmail = email;
      
      // Show loading state on button immediately
      const submitBtn = document.getElementById('class-signup-auth-email-submit');
      if (submitBtn) {
        submitBtn.classList.add('class-signup-popup__btn--loading');
        submitBtn.disabled = true;
      }
      
      console.log('📅 Gateway: Checking subscription for:', email);
      
      try {
        // Show verifying state
        this.showState('verifying');
        this.updateVerifyingStatus('Checking your VIP membership...');
        
        // Check subscription status via proxy with timeout
        const hasQuarterly = await this.checkSubscriptionByEmailFast(email);
        
        // Cache result
        this.setCachedSubscriptionStatus(hasQuarterly);
        
        // Show result immediately - no artificial delay
        if (hasQuarterly) {
          console.log('📅 Gateway: User is a quarterly subscriber');
          this.showState('subscriber');
        } else {
          console.log('📅 Gateway: User is NOT a quarterly subscriber');
          this.showState('non-subscriber');
        }
        
      } catch (error) {
        console.error('📅 Gateway: Subscription check failed:', error);
        // Show error state with retry option instead of defaulting to non-subscriber
        this.showState('error');
      } finally {
        // Reset button state
        if (submitBtn) {
          submitBtn.classList.remove('class-signup-popup__btn--loading');
          submitBtn.disabled = false;
        }
      }
    },
    
    /**
     * Check subscription status by email via Skio proxy
     * Used for non-logged-in users who enter their email
     * 
     * WORLD-CLASS UX:
     * - 20 second timeout (gives proxy time for retries)
     * - Progress updates every 3 seconds
     * - Throws on timeout/error (triggers retry UI)
     * - Only returns false for confirmed non-subscribers
     */
    async checkSubscriptionByEmail(email) {
      console.log('📅 Gateway: Checking subscription by email:', email);
      
      const controller = new AbortController();
      
      // Progress updates to reassure user - shows retry status
      // Backend has 20s timeout with 2 retries (3 attempts total)
      // Messages are timed to align with backend retry schedule
      const progressMessages = [
        { time: 0, message: 'Checking your VIP membership...' },
        { time: 3000, message: 'Connecting to subscription service...' },
        { time: 8000, message: 'Verifying your account...' },
        { time: 15000, message: 'Still working... (this may take a moment)' },
        { time: 22000, message: 'Retrying connection... (attempt 2 of 3)' },
        { time: 35000, message: 'Almost there... (attempt 3 of 3)' },
        { time: 50000, message: 'Finalizing verification...' },
        { time: 60000, message: 'Just a moment longer...' }
      ];
      
      const progressTimeouts = progressMessages.map(({ time, message }) => 
        setTimeout(() => this.updateVerifyingStatus(message), time)
      );
      
      // Timeout handler - uses config from proxy (or fallback)
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, this.REQUEST_TIMEOUT_MS);
      
      try {
        const response = await fetch(this.PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            query: `
              query CheckQuarterly($email: String!) {
                Subscriptions(
                  where: {
                    StorefrontUser: { emailLower: { _eq: $email } },
                    status: { _eq: "ACTIVE" }
                  },
                  limit: 10
                ) {
                  status
                  BillingPolicy {
                    interval
                    intervalCount
                  }
                }
              }
            `,
            variables: { email: email.toLowerCase().trim() }
          })
        });

        clearTimeout(timeoutId);
        progressTimeouts.forEach(t => clearTimeout(t));

        // Server errors should trigger retry, not default to non-subscriber
        if (!response.ok) {
          console.log('📅 Gateway: Proxy HTTP error:', response.status);
          if (response.status >= 500) {
            throw new Error(`Server error: ${response.status}`);
          }
          // 4xx errors (like 429 rate limit) - throw to show retry
          throw new Error(`Request failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('📅 Gateway: Email check response:', JSON.stringify(data).substring(0, 200));
        
        // GraphQL errors should trigger retry
        if (data.errors) {
          console.log('📅 Gateway: GraphQL errors:', data.errors);
          throw new Error('Verification service error');
        }

        const subscriptions = data.data?.Subscriptions || [];
        console.log('📅 Gateway: Found', subscriptions.length, 'active subscriptions for email');
        
        // Only return false for CONFIRMED non-subscribers (successful response, no quarterly found)
        return this.hasQuarterlySubscription(subscriptions);
        
      } catch (error) {
        clearTimeout(timeoutId);
        progressTimeouts.forEach(t => clearTimeout(t));
        
        if (error.name === 'AbortError') {
          console.log('📅 Gateway: Request timed out after', this.REQUEST_TIMEOUT_MS, 'ms');
          throw new Error('Verification timed out - please try again');
        }
        
        console.log('📅 Gateway: Email subscription check failed:', error.message);
        throw error; // Re-throw to trigger error state with retry
      }
    },
    
    /**
     * Alias for backwards compatibility
     */
    async checkSubscriptionByEmailFast(email) {
      return this.checkSubscriptionByEmail(email);
    },
    
    // ═══════════════════════════════════════════════════════════════
    // SUBSCRIPTION VERIFICATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Get cached subscription status
     * Returns null if cache is invalid, stale, or login state has changed
     */
    getCachedSubscriptionStatus() {
      try {
        const cached = sessionStorage.getItem(this.CACHE_KEY);
        if (!cached) return null;

        const { result, email, isLoggedIn: cachedLoginState, customerId, timestamp } = JSON.parse(cached);

        // Validate: login state must match
        if (cachedLoginState !== this.isLoggedIn) {
          console.log('📅 Gateway: Cache invalid - login state changed');
          this.clearCache();
          return null;
        }

        // Validate: must be same email (if logged in)
        if (this.isLoggedIn && email !== this.customerEmail) {
          console.log('📅 Gateway: Cache invalid - email changed');
          this.clearCache();
          return null;
        }

        // Validate: must be same customer ID (if logged in)
        if (this.isLoggedIn && customerId && this.customerId && customerId !== this.customerId) {
          console.log('📅 Gateway: Cache invalid - customer ID changed');
          this.clearCache();
          return null;
        }

        // Validate: within TTL
        if (Date.now() - timestamp > this.CACHE_TTL) {
          console.log('📅 Gateway: Cache expired');
          this.clearCache();
          return null;
        }

        return result;
      } catch {
        this.clearCache();
        return null;
      }
    },

    /**
     * Cache subscription status
     * Stores login state alongside result to detect state changes on next load
     */
    setCachedSubscriptionStatus(result) {
      try {
        sessionStorage.setItem(this.CACHE_KEY, JSON.stringify({
          result,
          email: this.customerEmail,
          isLoggedIn: this.isLoggedIn,
          customerId: this.customerId,
          timestamp: Date.now()
        }));
      } catch {
        // sessionStorage might be full or disabled
      }
    },

    /**
     * Verify subscription status via multiple methods
     * OPTIMIZED: Runs checks in parallel, returns true if ANY method confirms subscription
     * GUARANTEED: Uses multiple fallback methods to ensure subscribers are never blocked
     */
    async verifySubscription() {
      // Method 1: Check customer tags (instant, from Liquid) - ALWAYS CHECK FIRST
      if (this.checkViaCustomerTags()) {
        console.log('📅 Gateway: ✓ Verified via customer tags (instant)');
        return true;
      }
      
      // Method 2 & 3: Run BOTH checks in PARALLEL - first true wins
      // This is fast AND reliable
      try {
        const result = await this.parallelSubscriptionChecks();
        return result;
      } catch (error) {
        console.log('📅 Gateway: All subscription checks failed:', error.message);
        throw error; // Re-throw to trigger error state with retry
      }
    },
    
    /**
     * Run multiple subscription checks in parallel with FULL VISIBILITY
     * 
     * WORLD-CLASS UX:
     * - Shows real-time status of each verification method
     * - 25 second timeout (enough for proxy retries)
     * - Visual progress indicators
     * - Clear success/failure states for each method
     */
    async parallelSubscriptionChecks() {
      console.log('📅 Gateway: Running parallel subscription checks...');
      
      // Track status of each check
      const checkStatus = {
        proxy: { status: 'checking', message: 'Checking subscription database...' },
        page: { status: 'checking', message: 'Checking account records...' }
      };
      
      // Update the UI with current status
      const updateStatusUI = () => {
        const proxyIcon = checkStatus.proxy.status === 'checking' ? '🔄' : 
                         checkStatus.proxy.status === 'success' ? '✓' : 
                         checkStatus.proxy.status === 'error' ? '⚠' : '○';
        const pageIcon = checkStatus.page.status === 'checking' ? '🔄' : 
                        checkStatus.page.status === 'success' ? '✓' : 
                        checkStatus.page.status === 'error' ? '⚠' : '○';
        
        const statusHtml = `
          <div style="text-align: left; max-width: 280px; margin: 0 auto;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 0.9rem;">
              <span style="width: 20px; text-align: center;">${proxyIcon}</span>
              <span style="color: ${checkStatus.proxy.status === 'success' ? '#22c55e' : checkStatus.proxy.status === 'error' ? '#f59e0b' : '#666'};">
                ${checkStatus.proxy.message}
              </span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; font-size: 0.9rem;">
              <span style="width: 20px; text-align: center;">${pageIcon}</span>
              <span style="color: ${checkStatus.page.status === 'success' ? '#22c55e' : checkStatus.page.status === 'error' ? '#f59e0b' : '#666'};">
                ${checkStatus.page.message}
              </span>
            </div>
          </div>
        `;
        
        if (this.elements.verifyingStatus) {
          this.elements.verifyingStatus.innerHTML = statusHtml;
        }
      };
      
      // Initial status
      updateStatusUI();
      
      // Run BOTH methods in parallel with status tracking
      const proxyPromise = this.checkViaProxy()
        .then(found => {
          checkStatus.proxy = { 
            status: found ? 'success' : 'done', 
            message: found ? 'VIP membership confirmed!' : 'No active subscription found'
          };
          updateStatusUI();
          if (found) console.log('📅 Gateway: ✓ Proxy API confirmed subscription');
          return { source: 'proxy', found, error: false };
        })
        .catch(err => {
          console.log('📅 Gateway: Proxy check error:', err.message);
          checkStatus.proxy = { status: 'error', message: 'Connection issue, retrying...' };
          updateStatusUI();
          return { source: 'proxy', found: false, error: true };
        });
      
      const pagePromise = this.checkViaSkioAccountPage()
        .then(found => {
          checkStatus.page = { 
            status: found ? 'success' : 'done', 
            message: found ? 'VIP membership confirmed!' : 'No subscription on file'
          };
          updateStatusUI();
          if (found) console.log('📅 Gateway: ✓ Skio page confirmed subscription');
          return { source: 'page', found, error: false };
        })
        .catch(err => {
          console.log('📅 Gateway: Page check error:', err.message);
          checkStatus.page = { status: 'error', message: 'Could not check account' };
          updateStatusUI();
          return { source: 'page', found: false, error: true };
        });
      
      return new Promise((resolve, reject) => {
        let completedCount = 0;
        let foundSubscription = false;
        let allErrored = true;
        
        const handleResult = (result) => {
          completedCount++;
          if (!result.error) allErrored = false;
          
          if (result.found && !foundSubscription) {
            foundSubscription = true;
            console.log('📅 Gateway: Subscription confirmed via', result.source);
            
            // Show success message briefly before resolving
            this.updateVerifyingStatus('✓ VIP membership verified!');
            setTimeout(() => resolve(true), 500);
            return;
          }
          
          // Only resolve/reject after BOTH checks complete
          if (completedCount === 2 && !foundSubscription) {
            if (allErrored) {
              // Both checks failed with errors - show retry
              console.log('📅 Gateway: All checks errored - showing retry');
              reject(new Error('Could not verify membership status'));
            } else {
              // At least one check succeeded but found no subscription
              console.log('📅 Gateway: No subscription found in any check');
              resolve(false);
            }
          }
        };
        
        proxyPromise.then(handleResult);
        pagePromise.then(handleResult);
        
        // Progress updates during long waits - aligned with backend retry schedule
        // Backend: 20s timeout per attempt, 2 retries = up to 60s total
        const progressUpdates = [
          { time: 5000, message: 'Connecting to subscription service...' },
          { time: 12000, message: 'Verifying your account...' },
          { time: 20000, message: 'Retrying connection... (attempt 2 of 3)' },
          { time: 35000, message: 'Almost there... (attempt 3 of 3)' },
          { time: 50000, message: 'Finalizing verification...' },
          { time: 60000, message: 'Just a moment longer...' }
        ];
        
        progressUpdates.forEach(({ time, message }) => {
          setTimeout(() => {
            if (!foundSubscription && completedCount < 2) {
              // Update any still-checking items
              if (checkStatus.proxy.status === 'checking') {
                checkStatus.proxy.message = message;
              }
              if (checkStatus.page.status === 'checking') {
                checkStatus.page.message = message;
              }
              updateStatusUI();
            }
          }, time);
        });
        
        // Safety timeout - reject after REQUEST_TIMEOUT_MS if something hangs
        setTimeout(() => {
          if (!foundSubscription && completedCount < 2) {
            console.log('📅 Gateway: Timeout after', this.REQUEST_TIMEOUT_MS, 'ms - showing retry option');
            checkStatus.proxy.status === 'checking' && (checkStatus.proxy = { status: 'error', message: 'Timed out' });
            checkStatus.page.status === 'checking' && (checkStatus.page = { status: 'error', message: 'Timed out' });
            updateStatusUI();
            
            setTimeout(() => {
              reject(new Error('Verification timed out - please try again'));
            }, 1000);
          }
        }, this.REQUEST_TIMEOUT_MS);
      });
    },

    /**
     * Check subscription via customer tags
     */
    checkViaCustomerTags() {
      const customerTags = window.vipGatewayConfig?.customerTags || [];
      
      // Tags that grant access
      const vipTags = ['vip', 'quarterly', 'quarterly-member', 'vip-member', '90-day-access'];
      
      const hasVipTag = customerTags.some(tag => 
        vipTags.some(vipTag => tag.toLowerCase().includes(vipTag))
      );
      
      if (hasVipTag) {
        console.log('📅 Gateway: Verified via customer tags');
      }
      
      return hasVipTag;
    },

    /**
     * Check subscription via Skio account page scraping
     */
    async checkViaSkioAccountPage() {
      try {
        console.log('📅 Gateway: Checking Skio account page...');
        
        const response = await fetch('/a/account/subscriptions', {
          method: 'GET',
          headers: { 'Accept': 'text/html' },
          credentials: 'include'
        });

        if (!response.ok) {
          console.log('📅 Gateway: Skio page returned', response.status);
          return false;
        }

        const html = await response.text();
        
        // Look for __NEXT_DATA__ JSON in the page
        const nextDataMatch = html.match(/<script id="__NEXT_DATA__"[^>]*>([\s\S]*?)<\/script>/);
        if (nextDataMatch) {
          try {
            const nextData = JSON.parse(nextDataMatch[1]);
            const pageProps = nextData?.props?.pageProps;
            
            if (pageProps) {
              const subscriptions = pageProps.subscriptions || 
                                   pageProps.customer?.subscriptions || 
                                   pageProps.data?.subscriptions;
              
              if (subscriptions?.length > 0) {
                console.log('📅 Gateway: Found subscriptions in page data:', subscriptions.length);
                
                // Check for quarterly subscription
                for (const sub of subscriptions) {
                  const status = (sub.status || '').toUpperCase();
                  if (status !== 'ACTIVE') continue;
                  
                  const frequency = parseInt(sub.orderIntervalFrequency || sub.order_interval_frequency || 0);
                  const unit = (sub.orderIntervalUnit || sub.order_interval_unit || '').toLowerCase();
                  
                  console.log('📅 Gateway: Subscription -', frequency, unit);
                  
                  if ((unit === 'week' || unit === 'weeks') && frequency === 12) {
                    console.log('📅 Gateway: Found quarterly (12 weeks)!');
                    return true;
                  }
                  if ((unit === 'month' || unit === 'months') && frequency === 3) {
                    console.log('📅 Gateway: Found quarterly (3 months)!');
                    return true;
                  }
                }
              }
            }
          } catch (e) {
            console.log('📅 Gateway: Could not parse __NEXT_DATA__:', e.message);
          }
        }
        
        // Fallback: Check for "Every 12 weeks" text in the HTML
        if (html.includes('Every 12 weeks') || html.includes('every 12 weeks')) {
          console.log('📅 Gateway: Found "Every 12 weeks" text in page');
          if (!html.includes('CANCELLED') && !html.includes('Cancelled')) {
            console.log('📅 Gateway: Quarterly verified via page content!');
            return true;
          }
        }
        
        return false;
      } catch (error) {
        console.log('📅 Gateway: Skio page check failed:', error.message);
        return false;
      }
    },

    /**
     * Check subscription via Skio proxy API
     * Returns true if user has an active quarterly subscription
     * 
     * OPTIMIZED: 20 second timeout to allow proxy retries
     * Throws on network/server errors (triggers retry UI)
     * Only returns false for confirmed non-subscribers
     */
    async checkViaProxy() {
      const email = this.customerEmail;
      if (!email) {
        console.log('📅 Gateway: Proxy check - no email available');
        throw new Error('No email available');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT_MS);

      try {
        console.log('📅 Gateway: Checking via Skio proxy for:', email);
        
        const response = await fetch(this.PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            query: `
              query CheckQuarterly($email: String!) {
                Subscriptions(
                  where: {
                    StorefrontUser: { emailLower: { _eq: $email } },
                    status: { _eq: "ACTIVE" }
                  },
                  limit: 10
                ) {
                  status
                  BillingPolicy {
                    interval
                    intervalCount
                  }
                }
              }
            `,
            variables: { email: email.toLowerCase().trim() }
          })
        });

        clearTimeout(timeoutId);

        // Server errors should throw (trigger retry), not return false
        if (!response.ok) {
          console.log('📅 Gateway: Proxy HTTP error:', response.status);
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();
        console.log('📅 Gateway: Proxy response:', JSON.stringify(data).substring(0, 200));
        
        // GraphQL errors should throw (trigger retry)
        if (data.errors) {
          console.log('📅 Gateway: GraphQL errors:', data.errors);
          throw new Error('Verification service error');
        }

        const subscriptions = data.data?.Subscriptions || [];
        console.log('📅 Gateway: Found', subscriptions.length, 'active subscriptions');
        
        const hasQuarterly = this.hasQuarterlySubscription(subscriptions);
        console.log('📅 Gateway: Has quarterly subscription:', hasQuarterly);
        
        // Only return false for CONFIRMED non-subscribers
        return hasQuarterly;
        
      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          console.log('📅 Gateway: Proxy request timed out after', this.REQUEST_TIMEOUT_MS, 'ms');
          throw new Error('Request timed out');
        }
        
        console.log('📅 Gateway: Proxy fetch failed:', error.message);
        throw error; // Re-throw to trigger retry UI
      }
    },

    /**
     * Check if subscriptions array contains a quarterly subscription
     * LENIENT: Accepts various quarterly formats (12 weeks, 3 months, 84-92 days)
     */
    hasQuarterlySubscription(subscriptions) {
      if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
        console.log('📅 Gateway: No subscriptions to check');
        return false;
      }
      
      console.log('📅 Gateway: Checking', subscriptions.length, 'subscriptions for quarterly...');
      
      for (const sub of subscriptions) {
        const status = (sub.status || '').toUpperCase();
        console.log('📅 Gateway: Subscription status:', status);
        
        if (status !== 'ACTIVE') {
          console.log('📅 Gateway: Skipping non-active subscription');
          continue;
        }
        
        const policy = sub.BillingPolicy || sub.billingPolicy || {};
        const interval = (policy.interval || '').toUpperCase();
        const count = parseInt(policy.intervalCount) || 0;
        
        console.log('📅 Gateway: Billing policy - interval:', interval, 'count:', count);
        
        // Quarterly = 12 weeks OR 3 months OR ~84-92 days
        const isQuarterly = (
          (interval === 'WEEK' && count >= 11 && count <= 13) ||  // ~12 weeks (allow some variance)
          (interval === 'WEEKS' && count >= 11 && count <= 13) ||
          (interval === 'MONTH' && count >= 2 && count <= 4) ||   // ~3 months (allow some variance)
          (interval === 'MONTHS' && count >= 2 && count <= 4) ||
          (interval === 'DAY' && count >= 80 && count <= 95) ||   // ~90 days
          (interval === 'DAYS' && count >= 80 && count <= 95)
        );
        
        if (isQuarterly) {
          console.log('📅 Gateway: ✓ FOUND quarterly subscription!', interval, count);
          return true;
        }
      }
      
      console.log('📅 Gateway: No quarterly subscription found in list');
      return false;
    },

    // ═══════════════════════════════════════════════════════════════
    // CONTENT POPULATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Populate popup content with module data
     */
    populateContent(moduleData) {
      // Hero image
      if (moduleData.expertImage && this.elements.heroImage) {
        this.elements.heroImage.src = moduleData.expertImage;
        this.elements.heroImage.alt = moduleData.expertName;
      }
      
      // Badge
      if (this.elements.badgeText) {
        this.elements.badgeText.textContent = moduleData.moduleLabel;
      }
      
      // Title
      if (this.elements.title) {
        this.elements.title.textContent = moduleData.sessionTitle;
      }
      
      // Expert info
      if (moduleData.expertImage && this.elements.expertAvatar) {
        this.elements.expertAvatar.src = moduleData.expertImage;
        this.elements.expertAvatar.alt = moduleData.expertName;
      }
      if (this.elements.expertName) {
        this.elements.expertName.textContent = moduleData.expertName;
      }
      if (this.elements.expertRole) {
        this.elements.expertRole.textContent = moduleData.expertRole;
      }
      
      // Description
      if (this.elements.description) {
        this.elements.description.textContent = moduleData.description;
      }
      
      // Details
      if (this.elements.dateValue) {
        this.elements.dateValue.textContent = moduleData.dateText;
      }
      if (this.elements.durationValue) {
        this.elements.durationValue.textContent = moduleData.duration;
      }
    },

    // ═══════════════════════════════════════════════════════════════
    // FORM HANDLING (Subscriber State)
    // ═══════════════════════════════════════════════════════════════

    /**
     * Enable email editing
     */
    enableEmailEdit() {
      this.elements.emailInput.readOnly = false;
      this.elements.emailInput.classList.remove('class-signup-popup__input--verified');
      if (this.elements.verifiedBadge) {
        this.elements.verifiedBadge.style.display = 'none';
      }
      this.elements.emailInput.focus();
      this.elements.emailInput.select();
    },

    /**
     * Validate email in real-time
     */
    validateEmailRealtime() {
      const email = this.elements.emailInput.value.trim();
      
      if (email.length > 0 && email.includes('@')) {
        this.clearValidationError();
      }
      
      this.updateSubmitState();
    },

    /**
     * Validate email on blur
     */
    validateEmailOnBlur() {
      const email = this.elements.emailInput.value.trim();
      
      if (!email) {
        this.clearValidationError();
        return;
      }
      
      if (!this.validateEmail(email)) {
        this.showValidationError('Please enter a valid email address');
      } else {
        this.clearValidationError();
        this.elements.emailInput.classList.add('class-signup-popup__input--valid');
      }
    },

    /**
     * Validate email format
     */
    validateEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    /**
     * Show validation error
     */
    showValidationError(message) {
      const inputGroup = this.elements.emailInput.closest('.class-signup-popup__input-group');
      
      this.elements.emailInput.classList.add('class-signup-popup__input--error');
      this.elements.emailInput.classList.remove('class-signup-popup__input--valid');
      
      const existingError = inputGroup.querySelector('.class-signup-popup__error');
      if (existingError) {
        existingError.remove();
      }
      
      const errorEl = document.createElement('p');
      errorEl.className = 'class-signup-popup__error';
      errorEl.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        ${message}
      `;
      inputGroup.appendChild(errorEl);
      
      this.elements.emailInput.classList.add('class-signup-popup__input--shake');
      setTimeout(() => {
        this.elements.emailInput.classList.remove('class-signup-popup__input--shake');
      }, 500);
    },

    /**
     * Clear validation error
     */
    clearValidationError() {
      const inputGroup = this.elements.emailInput.closest('.class-signup-popup__input-group');
      
      this.elements.emailInput.classList.remove('class-signup-popup__input--error');
      
      const existingError = inputGroup.querySelector('.class-signup-popup__error');
      if (existingError) {
        existingError.classList.add('class-signup-popup__error--hiding');
        setTimeout(() => existingError.remove(), 200);
      }
    },

    /**
     * Update submit button state
     */
    updateSubmitState() {
      const email = this.elements.emailInput.value.trim();
      const isValid = this.validateEmail(email);
      
      this.elements.submitBtn?.classList.toggle('class-signup-popup__submit--ready', isValid && email.length > 0);
    },

    /**
     * Handle form submission
     * Routes through secure server-side proxy which re-verifies subscription
     */
    async handleSubmit() {
      const email = this.elements.emailInput.value.trim();
      
      if (!email) {
        this.showValidationError('Please enter your email address');
        this.elements.emailInput.focus();
        return;
      }
      
      if (!this.validateEmail(email)) {
        this.showValidationError('Please enter a valid email address');
        this.elements.emailInput.focus();
        return;
      }
      
      this.clearValidationError();
      
      this.elements.submitBtn.classList.add('class-signup-popup__submit--loading');
      this.elements.submitBtn.disabled = true;
      
      try {
        const result = await this.submitReservation(email);
        
        if (result.success) {
          this.showState('success');
        } else if (result.isSubscriber === false) {
          // Server rejected - not a subscriber (prevents client-side bypass)
          console.log('🚫 Server rejected: subscription verification failed');
          this.showState('non-subscriber');
        } else {
          // Other error - still show success if we got this far
          this.showState('success');
        }
      } catch (error) {
        console.error('❌ Submission error:', error);
        
        // Check if server explicitly rejected due to subscription
        if (error.message === 'SUBSCRIPTION_REQUIRED') {
          this.showState('non-subscriber');
        } else {
          // Network or other error - show generic error
          this.showValidationError('Something went wrong. Please try again.');
          this.elements.submitBtn.classList.remove('class-signup-popup__submit--loading');
          this.elements.submitBtn.disabled = false;
        }
      }
    },

    // ═══════════════════════════════════════════════════════════════
    // SECURE SERVER-SIDE RESERVATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Submit reservation via secure server-side proxy
     * 
     * SECURITY: The server re-verifies subscription status before allowing
     * the reservation. This prevents client-side bypasses like:
     * - Console manipulation (showState('subscriber'))
     * - SessionStorage cache poisoning
     * - Direct Klaviyo API calls
     * 
     * @param {string} email - User's email address
     * @returns {Promise<{success: boolean, isSubscriber: boolean}>}
     */
    async submitReservation(email) {
      console.log('🔒 Submitting reservation via secure proxy...');
      
      const response = await fetch(this.PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reserve-class',
          email: email,
          moduleData: this.currentModule,
          firstName: this.customerFirstName,
          lastName: this.customerLastName,
          locationData: this.locationData ? {
            city: this.locationData.$city,
            region: this.locationData.$region,
            country: this.locationData.$country,
            zip: this.locationData.$zip,
            latitude: this.locationData.$latitude,
            longitude: this.locationData.$longitude,
            timezone: this.locationData.$timezone
          } : null
        })
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 403 && result.isSubscriber === false) {
          // Server explicitly rejected - not a subscriber
          console.log('🚫 Server rejected: not a quarterly subscriber');
          throw new Error('SUBSCRIPTION_REQUIRED');
        }
        
        if (response.status === 429) {
          console.log('⏳ Rate limited - too many attempts');
          throw new Error('RATE_LIMITED');
        }
        
        console.error('❌ Server error:', result.error);
        throw new Error(result.error || 'Server error');
      }

      console.log('✅ Reservation confirmed via secure proxy');
      return result;
    },

    // ═══════════════════════════════════════════════════════════════
    // UTILITIES
    // ═══════════════════════════════════════════════════════════════

    /**
     * Fetch user location data via IP geolocation
     */
    async fetchLocationData() {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const geoData = await response.json();
          this.locationData = {
            $city: geoData.city,
            $region: geoData.region,
            $country: geoData.country_name,
            $country_code: geoData.country_code,
            $zip: geoData.postal,
            $latitude: geoData.latitude,
            $longitude: geoData.longitude,
            $timezone: geoData.timezone,
            ip_address: geoData.ip
          };
          console.log('📍 Location data fetched:', this.locationData.$city, this.locationData.$country);
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch location data:', error);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // URL PARAMETER AUTO-OPEN SUPPORT - CURRENTLY DISABLED
  // ═══════════════════════════════════════════════════════════════
  
  /**
   * Check URL parameters and auto-open popup if requested
   * 
   * Supported URL parameters:
   * - ?reserve=<expert-slug> - Opens popup for specific expert (e.g., ?reserve=dr-amy-shah)
   * - ?open-signup=<module-index> - Opens popup for specific module by index
   * - ?signup - Opens popup for first available module
   * 
   * Update ALLOWED_SLUGS when enabling/disabling sessions.
   */
  function checkUrlAndAutoOpen() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Currently allowed expert slugs for direct link access
    // Add/remove slugs here to control which sessions can be accessed via URL
    const ALLOWED_SLUGS = ['dr-suzanne-devkota'];
    
    // Check for ?reserve=<expert-slug> parameter
    const reserveParam = urlParams.get('reserve');
    if (reserveParam) {
      const slug = reserveParam.toLowerCase().trim();
      
      // Check if this slug is allowed
      if (!ALLOWED_SLUGS.includes(slug)) {
        console.log('📅 Direct link blocked: Session not currently open for reservation. Requested:', slug);
        return;
      }
      
      // Find the card with matching expert slug
      const card = document.querySelector(`.program-curriculum__card[data-expert-slug="${slug}"]`);
      if (card) {
        const moduleData = ClassSignupPopup.extractModuleData(card);
        
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          console.log('📅 Auto-opening popup via ?reserve= parameter for:', slug);
          ClassSignupPopup.open(moduleData);
        }, 500);
      } else {
        console.log('📅 Could not find curriculum card for expert:', slug);
      }
      return;
    }
    
    // Check for ?open-signup=<module-index> parameter
    const openSignupParam = urlParams.get('open-signup');
    if (openSignupParam) {
      const moduleIndex = openSignupParam.trim();
      const moduleData = ClassSignupPopup.getModuleDataByIndex(moduleIndex);
      
      if (moduleData) {
        // Verify this module is available (has Reserve Seat button)
        const card = document.querySelector(`.program-curriculum__card[data-module-index="${moduleIndex}"]`);
        const isAvailable = card?.querySelector('.program-curriculum__card-cta--active');
        
        if (isAvailable) {
          setTimeout(() => {
            console.log('📅 Auto-opening popup via ?open-signup= parameter for module:', moduleIndex);
            ClassSignupPopup.open(moduleData);
          }, 500);
        } else {
          console.log('📅 Module', moduleIndex, 'is not currently available for reservation');
        }
      }
      return;
    }
    
    // Check for ?signup parameter (opens first available module)
    if (urlParams.has('signup')) {
      const activeCard = document.querySelector('.program-curriculum__card-cta--active')?.closest('.program-curriculum__card');
      if (activeCard) {
        const moduleData = ClassSignupPopup.extractModuleData(activeCard);
        setTimeout(() => {
          console.log('📅 Auto-opening popup via ?signup parameter');
          ClassSignupPopup.open(moduleData);
        }, 500);
      } else {
        console.log('📅 No sessions are currently available for reservation');
      }
      return;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ClassSignupPopup.init();
      checkUrlAndAutoOpen();
    });
  } else {
    ClassSignupPopup.init();
    checkUrlAndAutoOpen();
  }

  // SECURITY: Do NOT expose ClassSignupPopup to window
  // This prevents console-based bypasses like:
  // - window.ClassSignupPopup.showState('subscriber')
  // - window.ClassSignupPopup.submitReservation('email')
})();
